import React from 'react'
import { clsx } from 'clsx'
import { Slot } from '@radix-ui/react-slot'
import type { ResponsiveLevel } from '../../theme/media'
import { type Sprinkles, sprinkles } from '../../theme/sprinkles.css'
import {
  balanceTextStyles,
  baseTextStyles,
  responsiveVariantStyles,
  textSingleLine,
  textStrikethrough,
  textUppercase,
} from './Typography.css'

type StandardSizes = '12' | '14' | '16' | '18' | '20' | '24' | '32' | '40' | '48' | '56' | '72' | '96'
type SerifSizes = Exclude<StandardSizes, '12' | '14' | '16' | '18'> | '28'

export type TypographyVariant = `standard.${StandardSizes}` | `serif.${SerifSizes}` | 'inherit'

export type TypographyProps = {
  variant?: TypographyVariant | Partial<Record<ResponsiveLevel, TypographyVariant>>
  color?: string // Direct theme token support
  align?: Sprinkles['textAlign']
  balance?: boolean
  singleLine?: boolean
  strikethrough?: boolean
  uppercase?: boolean
  asChild?: boolean
} & Omit<React.HTMLAttributes<HTMLElement>, 'color'>

export const Typography = React.forwardRef<HTMLParagraphElement, TypographyProps>(
  (
    {
      variant = 'standard.18',
      color,
      align,
      balance,
      singleLine,
      strikethrough,
      uppercase,
      className,
      style,
      children,
      asChild,
      ...rest
    }: TypographyProps,
    ref: React.ForwardedRef<HTMLParagraphElement>,
  ) => {
    const Component = asChild ? Slot : 'p'

    return (
      <Component
        ref={ref}
        className={clsx(
          baseTextStyles,
          responsiveVariantStyles(variant),
          sprinkles({
            ...(align ? { textAlign: align } : {}),
          }),
          balance && balanceTextStyles,
          singleLine && textSingleLine,
          strikethrough && textStrikethrough,
          uppercase && textUppercase,
          className,
        )}
        style={{
          ...(color ? { color } : {}),
          ...style,
        }}
        {...rest}
      >
        {children}
      </Component>
    )
  },
)

Typography.displayName = 'Typography'
